import React, { useState, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Icon from '../components/Icon';

const ALLOWED_DOMAIN = '@coynitt.ca';

// Map ?reason= query values (set by the 401 interceptor / ProtectedRoute
// timeout) to friendly one-liners so users understand why they were bounced.
//
// SECURITY (audit L-1, PR #14 originally): the raw `reason` query param
// is NEVER rendered to the DOM. Only the looked-up message from this map
// is. This prevents a reflected-XSS payload like
// /signin?reason=<img%20src=x%20onerror=…> from ever reaching JSX.
//
// If you add a new banner trigger, add it to this map — do not render
// `{reason}` directly. The map's lookup-and-default-to-null behaviour
// is the security boundary.
const REASON_MESSAGES = {
  session_expired:      'Your session expired. Please sign in again.',
  profile_load_failed:  'We couldn’t verify your account. Please sign in again.',
  mfa_required:         'Enter your authenticator code to finish signing in.',
};

const SignIn = () => {
  const { session, mfaRequired, signInWithPassword, signInWithMagicLink, signUp, challengeAndVerify, signOut } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState('password'); // password | magic | signup
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  // Read ?reason= once per render. We don't clear it from the URL — it's
  // harmless and lets a user reload the page and still see the banner.
  const reasonBanner = useMemo(() => {
    const q = new URLSearchParams(location.search);
    const reason = q.get('reason');
    return reason ? REASON_MESSAGES[reason] || null : null;
  }, [location.search]);

  // Fully authenticated (password + any required TOTP) → go to the dashboard.
  if (session && !mfaRequired) {
    const to = location.state?.from?.pathname || '/';
    return <Navigate to={to} replace />;
  }

  // Password verified but the account has a TOTP factor — hold here and take
  // the second factor before elevating the session to aal2.
  const submitMfa = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await challengeAndVerify(mfaCode.trim());
      // On success mfaRequired flips false and the guard above navigates on.
    } catch (err) {
      setError(err.message || 'That code didn’t match. Try the current one from your authenticator.');
    } finally {
      setBusy(false);
    }
  };

  if (session && mfaRequired) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg-canvas)', padding: 'var(--s6)' }}>
        <div className="card" style={{ width: '100%', maxWidth: 420, padding: 'var(--s8)' }}>
          <div className="row row-gap-3 mb-4" style={{ justifyContent: 'center' }}>
            <div className="sidebar-brand-mark" style={{ width: 40, height: 40, fontSize: 18 }}>C</div>
            <div className="col" style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--tx1)', letterSpacing: '-0.01em' }}>Two-factor authentication</div>
              <div className="text-xs" style={{ color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                Enter your 6-digit code
              </div>
            </div>
          </div>
          <p className="text-sm" style={{ color: 'var(--tx2)', marginBottom: 'var(--s4)', lineHeight: 1.6 }}>
            Open your authenticator app and enter the current 6-digit code for Coynitt Dashboard.
          </p>
          <form onSubmit={submitMfa}>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              autoFocus
              required
              style={{
                width: '100%', padding: '12px 14px', background: 'var(--bg-input)',
                border: '1px solid var(--bd)', borderRadius: 'var(--r-md)', color: 'var(--tx1)',
                fontSize: 22, letterSpacing: '0.35em', textAlign: 'center', outline: 'none',
                fontFamily: 'var(--font-mono, monospace)', marginBottom: 'var(--s3)',
              }}
            />
            {error && (
              <div style={{ background: 'var(--err-bg)', color: 'var(--err)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>
                {error}
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={busy || mfaCode.length !== 6} style={{ width: '100%', justifyContent: 'center', padding: '12px' }}>
              {busy ? 'Verifying…' : 'Verify & continue'}
            </button>
          </form>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={signOut}
            style={{ width: '100%', justifyContent: 'center', marginTop: 'var(--s3)', fontSize: 13 }}
          >
            Cancel and sign out
          </button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!email.toLowerCase().endsWith(ALLOWED_DOMAIN)) {
      setError(`Only ${ALLOWED_DOMAIN} email addresses can access the dashboard.`);
      return;
    }
    setBusy(true);
    try {
      if (mode === 'password') {
        await signInWithPassword(email, password);
      } else if (mode === 'magic') {
        await signInWithMagicLink(email);
        setInfo('Check your email for a sign-in link.');
      } else if (mode === 'signup') {
        await signUp(email, password);
        setInfo('Account created. Awaiting admin approval — you’ll receive an email once granted access.');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    background: 'var(--bg-input)',
    border: '1px solid var(--bd)',
    borderRadius: 'var(--r-md)',
    color: 'var(--tx1)',
    fontSize: 14,
    outline: 'none',
    fontFamily: 'var(--font)',
    marginBottom: 'var(--s3)',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg-canvas)', padding: 'var(--s6)' }}>
      <div className="card" style={{ width: '100%', maxWidth: 420, padding: 'var(--s8)' }}>
        <div className="row row-gap-3 mb-4" style={{ justifyContent: 'center' }}>
          <div className="sidebar-brand-mark" style={{ width: 40, height: 40, fontSize: 18 }}>C</div>
          <div className="col" style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--tx1)', letterSpacing: '-0.01em' }}>Coynitt Dashboard</div>
            <div className="text-xs" style={{ color: 'var(--tx3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
              Internal console
            </div>
          </div>
        </div>

        <div className="env-toggle" style={{ width: '100%', justifyContent: 'space-between', marginBottom: 'var(--s5)' }}>
          {[
            { id: 'password', label: 'Password' },
            { id: 'magic',    label: 'Magic link' },
            { id: 'signup',   label: 'Sign up' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              className={mode === t.id ? 'active' : ''}
              onClick={() => { setMode(t.id); setError(null); setInfo(null); }}
              style={{ flex: 1 }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {reasonBanner && !error && !info && (
          <div
            role="alert"
            style={{
              background: 'var(--warn-bg, rgba(234,179,8,0.10))',
              color: 'var(--warn, #d97706)',
              border: '1px solid var(--warn, #d97706)',
              borderRadius: 'var(--r-md)',
              padding: 'var(--s3)',
              fontSize: 13,
              lineHeight: 1.5,
              marginBottom: 'var(--s4)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Icon name="clock" size={14} />
            <span>{reasonBanner}</span>
          </div>
        )}

        <form onSubmit={submit}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={`yourname${ALLOWED_DOMAIN}`}
            required
            autoComplete="email"
            style={inputStyle}
          />
          {mode !== 'magic' && (
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              minLength={8}
              style={inputStyle}
            />
          )}

          {error && (
            <div style={{ background: 'var(--err-bg)', color: 'var(--err)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>
              {error}
            </div>
          )}
          {info && (
            <div style={{ background: 'var(--ok-bg)', color: 'var(--ok)', padding: 'var(--s3)', borderRadius: 'var(--r-md)', fontSize: 13, marginBottom: 'var(--s3)' }}>
              {info}
            </div>
          )}

          <button type="submit" className="btn btn-primary" disabled={busy} style={{ width: '100%', justifyContent: 'center', padding: '12px' }}>
            {busy ? 'Working…' : (
              mode === 'password' ? 'Sign in' :
              mode === 'magic'    ? 'Send magic link' :
                                    'Create account'
            )}
          </button>
        </form>

        <div className="text-xs" style={{ marginTop: 'var(--s4)', color: 'var(--tx3)', textAlign: 'center', lineHeight: 1.6 }}>
          <Icon name="shield" size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Access restricted to {ALLOWED_DOMAIN} accounts.
          <br />
          New sign-ups require admin approval.
        </div>
      </div>
    </div>
  );
};

export default SignIn;
